import {
  AppCard,
  Button,
  Icon,
  PageContainer,
  RouteButtonLink,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { mdiArrowLeft, mdiRefresh } from "@mdi/js";
import {
  Alert,
  Box,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { createFileRoute } from "@tanstack/react-router";
import { isAxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { useShowCallOffContractItem } from "../../../../__generated__/orval/endpoints/call-off-contract-items/call-off-contract-items";
import { EntitiesCallOffContractItemMemberMain } from "../../../../__generated__/orval/models/entitiesCallOffContractItemMemberMain.zod";
import { ForbiddenError } from "../../../../errors/ForbiddenError";
import { searchSchema } from "../issue_purchase_order";

const CallOffContractServiceLines = () => {
  const { callOffItemId } = Route.useParams();
  const search = Route.useSearch();
  const { i18n, t } = useTranslation();
  const { data, error, isError, isFetching, isPending, refetch } =
    useShowCallOffContractItem(callOffItemId, { query: { retry: false } });
  const parsed = EntitiesCallOffContractItemMemberMain.safeParse(data);
  const responseStatus = isAxiosError(error)
    ? error.response?.status
    : undefined;
  const errorMessage =
    responseStatus === 403
      ? t("callOffContract.serviceLines.accessDeniedMessage")
      : responseStatus === 404
        ? t("callOffContract.serviceLines.notFoundMessage")
        : t("callOffContract.serviceLines.loadError");
  const priceFormat = new Intl.NumberFormat(i18n.language, {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  });
  const quantityFormat = new Intl.NumberFormat(i18n.language, {
    maximumFractionDigits: 3,
  });

  return (
    <PageContainer
      maxWidth="xl"
      showBackButton={false}
      title={t("callOffContract.serviceLines.title")}
    >
      <AppCard>
        <Box sx={{ mb: 2 }}>
          <RouteButtonLink
            search={search}
            startIcon={<Icon icon={mdiArrowLeft} />}
            to="/call_off_contract_items/issue_purchase_order"
            variant="outlined"
          >
            {t("common.back")}
          </RouteButtonLink>
        </Box>

        {isError || (!isPending && !parsed.success) ? (
          <Alert
            action={
              responseStatus !== 403 && responseStatus !== 404 ? (
                <Button
                  disabled={isFetching}
                  onClick={() => void refetch()}
                  startIcon={<Icon icon={mdiRefresh} />}
                  variant="text"
                >
                  {t("callOffContract.serviceLines.retryButton")}
                </Button>
              ) : undefined
            }
            severity="error"
          >
            {errorMessage}
          </Alert>
        ) : isPending || !parsed.success ? (
          <Skeleton height={160} variant="rectangular" />
        ) : (
          <>
            <Box
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                display: "flex",
                flexWrap: "wrap",
                gap: 3,
                mb: 2,
                pb: 2,
              }}
            >
              {[
                {
                  label: t("callOffContract.prNumber"),
                  value: `${parsed.data.pr_number} / ${parsed.data.item_number}`,
                },
                {
                  label: t("callOffContract.referenceNumber"),
                  value: parsed.data.reference_number,
                },
                {
                  label: t("callOffContract.currency"),
                  value: parsed.data.currency || "-",
                },
              ].map(({ label, value }) => (
                <Box key={label} sx={{ minWidth: 160 }}>
                  <Typography color="text.secondary" variant="body2">
                    {label}
                  </Typography>
                  <Typography>{value}</Typography>
                </Box>
              ))}
            </Box>

            <TableContainer>
              <Table
                aria-label={t("callOffContract.serviceLines.title")}
                sx={{ minWidth: 800 }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>
                      {t("callOffContract.serviceLines.lineNumberLabel")}
                    </TableCell>
                    <TableCell>
                      {t("callOffContract.serviceLines.serviceNumberLabel")}
                    </TableCell>
                    <TableCell>{t("callOffContract.description")}</TableCell>
                    <TableCell align="right">{t("common.quantity")}</TableCell>
                    <TableCell>
                      {t("callOffContract.serviceLines.measureUnitLabel")}
                    </TableCell>
                    <TableCell align="right">
                      {t("callOffContract.serviceLines.netValueLabel")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {parsed.data.service_lines.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6}>
                        {t("callOffContract.serviceLines.emptyMessage")}
                      </TableCell>
                    </TableRow>
                  ) : (
                    parsed.data.service_lines.map((line) => (
                      <TableRow key={line.id}>
                        <TableCell>{line.service_line_number_ext}</TableCell>
                        <TableCell>{line.service_number ?? "-"}</TableCell>
                        <TableCell>{line.short_text}</TableCell>
                        <TableCell align="right">
                          {quantityFormat.format(Number(line.quantity))}
                        </TableCell>
                        <TableCell>{line.measure_unit || "-"}</TableCell>
                        <TableCell align="right">
                          {priceFormat.format(Number(line.net_value))}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </AppCard>
    </PageContainer>
  );
};

export const Route = createFileRoute(
  "/_private/call_off_contract_items/$callOffItemId/service_lines",
)({
  beforeLoad({ context }) {
    if (context.auth.user?.roles_names.includes("Call-Off Contract - View")) {
      return;
    }

    throw new ForbiddenError();
  },
  component: CallOffContractServiceLines,
  validateSearch: searchSchema,
});
